import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, X, Send, Bot, User, Minimize2, Maximize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatBotProps {
  className?: string;
}

const ChatBot: React.FC<ChatBotProps> = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const apiKey = 'AIzaSyCV86WhAFKGSLLHKfa3QE4GDm20xX2mQuE';
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addWelcomeMessage();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const addWelcomeMessage = () => {
    const welcomeMsg = `👋 Hi! I'm your CRM & Email Marketing specialist. I can help you with:

📧 **Email Marketing**: Campaigns, templates, deliverability, automation
💼 **CRM Systems**: Setup, optimization, integrations (Salesforce, HubSpot, etc.)
🔄 **Automation**: Workflows, lead scoring, drip campaigns
💻 **Scripts**: API integrations, custom automation, data tools

What would you like to know about?`;

    setMessages([{
      role: 'assistant',
      content: welcomeMsg,
      timestamp: new Date()
    }]);
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef.current?.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  const formatMessage = (text: string) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-sm">$1</code>')
      .replace(/```([\s\S]*?)```/g, '<pre class="bg-muted p-2 rounded text-sm overflow-x-auto"><code>$1</code></pre>')
      .replace(/\n/g, '<br>');
  };

  const getCRMResponse = async (userMessage: string): Promise<string> => {
    try {
      const context = messages.length > 0 
        ? messages.slice(-8).map(msg => 
            `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
          ).join('\n\n')
        : '';

      const fullPrompt = `You are a specialized CRM and Email Marketing expert assistant for AD Email Lab. Your expertise covers:

🎯 EMAIL MARKETING:
- Campaign strategy and execution
- Email templates and copywriting
- Deliverability and inbox placement
- A/B testing and optimization
- List segmentation and personalization
- Email automation sequences

💼 CRM SYSTEMS:
- Platform selection and setup (Salesforce, HubSpot, Pipedrive, Zoho, Monday.com)
- Data management and hygiene
- Custom fields and workflows
- Integration strategies
- User adoption and training

🔄 AUTOMATION & WORKFLOWS:
- Drip campaigns and nurture sequences
- Lead scoring and qualification
- Trigger-based automation
- API integrations and webhooks
- Zapier and automation platforms

💻 SCRIPTS & DEVELOPMENT:
- CRM API scripts and examples
- Data import/export tools
- Custom automation scripts
- Integration code (Python, JavaScript, etc.)
- Database queries and reporting

🛠️ AD EMAIL LAB TOOLS:
When users ask about our Email Builder or Image Editor tools, provide these detailed explanations:

**EMAIL BUILDER TOOL (https://ad-email-lab-builder.lovable.app/):**
Our Email Builder is a powerful drag-and-drop tool for creating professional email templates:

✨ **Key Features:**
- Drag-and-drop interface for easy email creation
- Pre-built responsive templates optimized for all devices
- Real-time preview across different email clients
- Built-in CRM integration capabilities
- Advanced customization options (fonts, colors, layouts)
- Export options: HTML, CSS, and various email platform formats

📋 **How to Use:**
1. Access the Email Builder at https://ad-email-lab-builder.lovable.app/
2. Choose from our library of professional templates or start from scratch
3. Drag elements (text, images, buttons, dividers) into your design
4. Customize styling, colors, and fonts to match your brand
5. Preview your email across desktop, mobile, and different email clients
6. Test deliverability and spam score
7. Export your template or integrate directly with your CRM

💡 **Best Practices:**
- Keep subject lines under 50 characters
- Use a clear call-to-action button
- Optimize images for fast loading
- Test across multiple email clients
- Ensure mobile responsiveness

**IMAGE EDITOR TOOL (https://ad-email-image-editor.lovable.app/):**
Our Image Editor specializes in creating email-optimized graphics:

✨ **Key Features:**
- Email-specific image optimization
- Template library for common email graphics (headers, banners, social media)
- Automatic compression for faster email loading
- Brand color palette integration
- Multiple export formats optimized for email clients

📋 **How to Use:**
1. Access the Image Editor at https://ad-email-image-editor.lovable.app/
2. Upload your images or choose from our template library
3. Edit, crop, and optimize images for email use
4. Apply brand colors and maintain consistent styling
5. Compress images to optimal file sizes for email delivery
6. Export in email-friendly formats (JPG, PNG, WebP)
7. Integrate directly into your email campaigns

💡 **Email Image Best Practices:**
- Keep file sizes under 100KB per image
- Use alt text for accessibility
- Maintain aspect ratios for mobile viewing
- Test image loading across email clients
- Consider fallback options for blocked images

Always provide practical, actionable advice with specific examples when relevant. When users ask about these tools, guide them step-by-step and offer tips for maximizing their effectiveness.

${context ? `Previous conversation:\n${context}\n\n` : ''}Current question: ${userMessage}`;

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: fullPrompt
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 1500,
          },
          safetySettings: [
            {
              category: "HARM_CATEGORY_HARASSMENT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_HATE_SPEECH",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_DANGEROUS_CONTENT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            }
          ]
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`API Error ${response.status}: ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        return data.candidates[0].content.parts[0].text;
      } else {
        throw new Error('No response generated');
      }

    } catch (error) {
      console.error('Gemini API Error:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('403')) {
          return "❌ Invalid API key. Please check your Google Gemini API key configuration.";
        } else if (error.message.includes('429')) {
          return "⏳ Rate limit reached. Please wait a moment before sending another message.";
        } else if (error.message.includes('400')) {
          return "⚠️ Request error. Please try rephrasing your question.";
        }
      }
      
      return "🔧 Connection error. Please check your internet connection and try again.";
    }
  };

  const sendMessage = async () => {
    const message = inputValue.trim();
    
    if (!message || isTyping) return;

    // Add user message
    const userMessage: Message = {
      role: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      // Get AI response
      const response = await getCRMResponse(message);
      
      // Add bot response
      const botMessage: Message = {
        role: 'assistant',
        content: response,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botMessage]);
      
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={cn(
      "fixed z-50",
      isMobile 
        ? "inset-x-4 bottom-4" 
        : "bottom-6 right-6",
      className
    )}>
      {!isOpen ? (
        <div className="flex flex-col items-end gap-2">
          {/* Minimize Button - only show when not minimized */}
          {!isMinimized && (
            <Button
              onClick={() => setIsMinimized(true)}
              variant="outline"
              size="sm"
              className="bg-background/80 backdrop-blur-sm hover:bg-background/90 border-red-500 border-2 hover:border-red-600"
            >
              <Minimize2 className="h-3 w-3" />
            </Button>
          )}
          
          {/* Main Chat Button - hide when minimized */}
          {!isMinimized && (
            <Button
              onClick={() => setIsOpen(true)}
              className={cn(
                "rounded-full bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group",
                isMobile ? "h-16 w-16" : "h-20 w-20"
              )}
              size="icon"
            >
              <Bot className={cn(
                "group-hover:scale-110 transition-transform duration-200",
                isMobile ? "h-10 w-10" : "h-14 w-14"
              )} style={{ animation: 'bounce 3s infinite' }} />
            </Button>
          )}
          
          {/* Restore Button - only show when minimized */}
          {isMinimized && (
            <Button
              onClick={() => setIsMinimized(false)}
              variant="outline"
              size="sm"
              className="bg-background/80 backdrop-blur-sm hover:bg-background/90 border-red-500 border-2 hover:border-red-600"
            >
              <Maximize2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      ) : (
        <div className={cn(
          "bg-background border border-border rounded-lg shadow-2xl flex flex-col animate-scale-in",
          isMobile 
            ? "w-full h-[85vh] max-h-[600px]" 
            : "w-96 h-[32rem]"
        )}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">CRM Assistant</h3>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  setIsOpen(false);
                  setIsMinimized(true);
                }}
                variant="outline"
                size="icon"
                className="h-8 w-8 border-red-500 border-2 hover:border-red-600"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => setIsOpen(false)}
                variant="ghost"
                size="icon"
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea ref={scrollRef} className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex gap-2",
                    message.role === 'user' ? "justify-end" : "justify-start"
                  )}
                >
                  {message.role === 'assistant' && (
                    <Bot className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  )}
                  <div
                    className={cn(
                      "max-w-[80%] p-3 rounded-lg text-sm",
                      message.role === 'user'
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-foreground"
                    )}
                    dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                  />
                  {message.role === 'user' && (
                    <User className="h-6 w-6 text-muted-foreground mt-1 flex-shrink-0" />
                  )}
                </div>
              ))}
              
              {isTyping && (
                <div className="flex gap-2 justify-start">
                  <Bot className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div className="bg-muted p-3 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about CRM, email marketing..."
                className="flex-1"
                disabled={isTyping}
              />
              <Button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isTyping}
                size="icon"
                className="flex-shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;